// The 10-day setter curriculum grouped by day, with a checkbox per item (saved to /api/training/progress).
// Items with no URL (playbook reading, drills, role-plays) link to the playbook section or the Practice tab.
import { useState } from 'preact/hooks';
import type { TrainingItem } from '../../../src/shared/api';
import type { CurriculumResponse, ProgressResponse } from '../../../src/shared/api-ai';
import { api, errorMessage } from '../api';
import { navigate } from '../app';
import { Empty, ErrorBanner } from '../components/ui';
import { internalTarget } from './markdown';
import { PLAYBOOK_SECTIONS, playbookPath } from './Playbook';

const KIND_LABEL: Record<TrainingItem['kind'], string> = {
  watch: 'Watch', read: 'Read', listen: 'Listen', drill: 'Drill', roleplay: 'Role-play', quiz: 'Quiz', shadow: 'Shadow',
};

function isHttpUrl(url: string): boolean {
  try { const u = new URL(url); return u.protocol === 'https:' || u.protocol === 'http:'; } catch { return false; }
}

function InternalLink(props: { item: TrainingItem }) {
  const t = internalTarget(props.item, PLAYBOOK_SECTIONS);
  if (!t) return null;
  const path = t.tab === 'practice' ? '/training' : playbookPath(t.section);
  return (
    <a class="tr-goto" href={path} onClick={(e) => { e.preventDefault(); navigate(path); }}>{t.label} →</a>
  );
}

export function Curriculum(props: { data: CurriculumResponse; onChange: (d: CurriculumResponse) => void }) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const { items } = props.data;

  if (items.length === 0) return <Empty>The curriculum hasn't been published yet.</Empty>;

  const toggle = async (item: TrainingItem, done: boolean) => {
    setSaving(item.key); setError(null);
    try {
      const r = await api.post<ProgressResponse>('/api/training/progress', { itemKey: item.key, done });
      const next = items.map((i) => (i.key === r.itemKey ? { ...i, doneAt: r.doneAt } : i));
      props.onChange({ ...props.data, items: next, done: next.filter((i) => i.doneAt).length });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(null);
    }
  };

  const days = [...new Set(items.map((i) => i.day))].sort((a, b) => a - b);
  return (
    <div class="stack">
      <div class="row" style="gap:12px">
        <span class="muted">{props.data.done} of {props.data.total} done · about {Math.round(props.data.totalMinutes / 60)} h in total</span>
        <div class="tr-bar ok" style="width:200px"><span style={`width:${props.data.total ? Math.round((props.data.done / props.data.total) * 100) : 0}%`} /></div>
      </div>
      <ErrorBanner error={error} />
      {days.map((d) => {
        const dayItems = items.filter((i) => i.day === d);
        const doneCount = dayItems.filter((i) => i.doneAt).length;
        return (
          <div class="card tr-day">
            <h3><span>Day {d}</span><span class="muted" style="font-size:13px;font-weight:500">{doneCount}/{dayItems.length} · {dayItems.reduce((s, i) => s + i.minutes, 0)} min</span></h3>
            {dayItems.map((i) => (
              <div class={`tr-item ${i.doneAt ? 'done' : ''}`}>
                <input type="checkbox" checked={!!i.doneAt} disabled={saving === i.key} aria-label={`Mark "${i.title}" done`}
                  onChange={(e) => toggle(i, (e.currentTarget as HTMLInputElement).checked)} />
                <div class="stack" style="gap:2px">
                  <div class="tr-title">
                    {i.url && isHttpUrl(i.url) ? <a href={i.url} target="_blank" rel="noopener noreferrer">{i.title}</a> : i.title}
                  </div>
                  {i.url ? null : <InternalLink item={i} />}
                  <div class="tr-why">{i.why}</div>
                </div>
                <div class="row" style="gap:4px"><span class="chip">{KIND_LABEL[i.kind] ?? i.kind}</span><span class="mono muted">{i.minutes} min</span></div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
